// This file is part of Indico.
// Copyright (C) 2002 - 2023 CERN
//
// Indico is free software; you can redistribute it and/or
// modify it under the terms of the MIT License; see the
// LICENSE file for more details.

import {Translate} from 'indico/react/i18n';
import {getPluginObjects} from 'indico/utils/plugins';

import AccommodationInput, {
  AccommodationSettings,
  accommodationSettingsInitialData,
  accommodationSettingsFormValidator,
} from './AccommodationInput';
import AccompanyingPersonsInput, {
  AccompanyingPersonsSettings,
  accompanyingPersonsSettingsInitialData,
} from './AccompanyingPersonsInput';
// TEMA: Added special field for additional participants (accompanying persons with full data and a single-choice input)
import AdditionalParticipantsInput, {
  AdditionalParticipantsSettings,
  additionalParticipantsSettingsInitialData,
} from './AdditionalParticipantsInput';
// TEMA END: Added special field for additional participants (accompanying persons with full data and a single-choice input)
import BooleanInput, {BooleanSettings} from './BooleanInput';
import CheckboxInput, {CheckboxSettings} from './CheckboxInput';
import {choiceFieldsSettingsFormDecorator} from './ChoicesSetup';
// TEMA: Added special field for checkboxes, that are only shown based a selection of another field
import ConditionalCheckboxInput, {ConditionalCheckboxSettings} from './ConditionalCheckboxInput';
// TEMA END: Added special field for checkboxes, that are only shown based a selection of another field
import CountryInput, {CountrySettings} from './CountryInput';
import DateInput, {
  DateSettings,
  dateSettingsFormDecorator,
  dateSettingsInitialData,
} from './DateInput';
import EmailInput from './EmailInput';
import FileInput from './FileInput';
// TEMA: Added special Invoice Recipient Input
import InvoiceRecipientInput from './InvoiceRecipientInput';
import LabelInput from './LabelInput';
import MultiChoiceInput, {
  MultiChoiceSettings,
  multiChoiceSettingsInitialData,
} from './MultiChoiceInput';
import NumberInput, {NumberSettings, numberSettingsFormValidator} from './NumberInput';
import PhoneInput from './PhoneInput';
import SingleChoiceInput, {
  SingleChoiceSettings,
  singleChoiceSettingsFormDecorator,
  singleChoiceSettingsInitialData,
} from './SingleChoiceInput';
import TextAreaInput, {TextAreaSettings} from './TextAreaInput';
import TextInput, {TextSettings, textSettingsFormValidator} from './TextInput';

/*
Available keys:
- title: required; used to show the field type when adding a new field
- icon: required; used in the add field dropdown (this uses an indico icon, not a SUI icon)
- inputComponent: required; the component used to render the field
- settingsComponent: optional; used if the field has custom settings
- settingsModalSize: optional; used if the field has settings which benefit
  from a larger modal size than "tiny"
- settingsFormDecorators: optional; the final-form decorators to apply to the
  settings form
- settingsFormValidator: optional, a function for final-form's form-level validation
- settingsFormInitialData: optional; initial data to use when creating a new
  field in case some of the settings need to be initialized. if it's a callable, the
  staticData from the state will be passed to it
- noLabel: optional; render the field without a label on the left
- noRequired: optional; hide the option to make the field required
- alwaysRequired: optional; always display the field as required
- hasPrice: optional; show price field if the whole field can have a price attached
- noRetentionPeriod: optional; hide the retention period setting
*/

const fieldRegistry = {
  label: {
    title: Translate.string('Label'),
    inputComponent: LabelInput,
    noRequired: true,
    noRetentionPeriod: true,
    noLabel: true,
    icon: 'tag',
    customFormItem: true,
  },
  text: {
    title: Translate.string('Text field'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  textarea: {
    title: Translate.string('Text area'),
    inputComponent: TextAreaInput,
    settingsComponent: TextAreaSettings,
    icon: 'textarea',
  },
  number: {
    title: Translate.string('Number'),
    inputComponent: NumberInput,
    settingsComponent: NumberSettings,
    settingsFormValidator: numberSettingsFormValidator,
    hasPrice: true,
    icon: 'seven-segment9',
  },
  checkbox: {
    title: Translate.string('Checkbox'),
    inputComponent: CheckboxInput,
    settingsComponent: CheckboxSettings,
    noLabel: true,
    hasPrice: true,
    icon: 'checkbox-checked',
    customFormItem: true,
  },
  date: {
    title: Translate.string('Date'),
    inputComponent: DateInput,
    settingsComponent: DateSettings,
    settingsFormDecorators: [dateSettingsFormDecorator],
    settingsFormInitialData: dateSettingsInitialData,
    icon: 'calendar',
  },
  bool: {
    title: Translate.string('Yes/No'),
    inputComponent: BooleanInput,
    settingsComponent: BooleanSettings,
    hasPrice: true,
    icon: 'switchon',
  },
  phone: {
    title: Translate.string('Phone'),
    inputComponent: PhoneInput,
    icon: 'phone',
  },
  country: {
    title: Translate.string('Country'),
    inputComponent: CountryInput,
    settingsComponent: CountrySettings,
    icon: 'earth',
  },
  file: {
    title: Translate.string('File'),
    inputComponent: FileInput,
    icon: 'upload',
  },
  email: {
    title: Translate.string('Email'),
    inputComponent: EmailInput,
    icon: 'mail',
  },
  single_choice: {
    title: Translate.string('Single Choice'),
    inputComponent: SingleChoiceInput,
    settingsComponent: SingleChoiceSettings,
    settingsModalSize: 'small',
    settingsFormDecorators: [choiceFieldsSettingsFormDecorator, singleChoiceSettingsFormDecorator],
    settingsFormInitialData: singleChoiceSettingsInitialData,
    icon: 'dropmenu',
  },
  multi_choice: {
    title: Translate.string('Multiple Choice'),
    inputComponent: MultiChoiceInput,
    settingsComponent: MultiChoiceSettings,
    settingsModalSize: 'small',
    settingsFormDecorators: [choiceFieldsSettingsFormDecorator],
    settingsFormInitialData: multiChoiceSettingsInitialData,
    icon: 'list',
  },
  accommodation: {
    title: Translate.string('Accommodation'),
    inputComponent: AccommodationInput,
    settingsComponent: AccommodationSettings,
    settingsModalSize: 'small',
    settingsFormDecorators: [choiceFieldsSettingsFormDecorator],
    settingsFormInitialData: accommodationSettingsInitialData,
    settingsFormValidator: accommodationSettingsFormValidator,
    noRequired: true,
    alwaysRequired: true,
    icon: 'home',
  },
  accompanying_persons: {
    title: Translate.string('Accompanying Persons'),
    inputComponent: AccompanyingPersonsInput,
    settingsComponent: AccompanyingPersonsSettings,
    settingsFormInitialData: accompanyingPersonsSettingsInitialData,
    settingsModalSize: 'tiny',
    noRequired: true,
    hasPrice: true,
    icon: 'user',
  },
  // TEMA: Added a checkbox that is displayed based on a single_choice selection
  conditional_checkbox: {
    title: Translate.string('Conditional Checkbox'),
    inputComponent: ConditionalCheckboxInput,
    settingsComponent: ConditionalCheckboxSettings,
    noLabel: true,
    hasPrice: true,
    icon: 'checkbox-checked',
    customFormItem: true,
  },
  // TEMA END: Added a checkbox that is displayed based on a single_choice selection
  // TEMA: Added special field for additional participants (accompanying persons with full data and a single-choice input)
  additional_participants: {
    title: Translate.string('Additional Participants'),
    inputComponent: AdditionalParticipantsInput,
    settingsComponent: AdditionalParticipantsSettings,
    settingsFormInitialData: additionalParticipantsSettingsInitialData,
    settingsModalSize: 'small',
    // noRequired: true,
    // hasPrice: true,
    icon: 'user',
  },
  // TEMA END: Added special field for additional participants (accompanying persons with full data and a single-choice input)
  // TEMA: Added special field for PO-Numbers
  po_number: {
    title: Translate.string('PO-Number'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  // TEMA END: Added special field for PO-Numbers
  // TEMA: Added special field for VAT-Numbers
  vat_number: {
    title: Translate.string('VAT-Number'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  // TEMA END: Added special field for VAT-Numbers
  // TEMA: Added special field for Rechungsadresse/Invoice Recipient
  invoice_recipient: {
    title: Translate.string('Billing Address'),
    inputComponent: InvoiceRecipientInput,
    settingsComponent: TextAreaSettings,
    icon: 'textarea',
  },
  // TEMA END: Added special field for Rechungsadresse/Invoice Recipient
  // TEMA: Added special field for Rechungsadresse/Invoice Recipient
  invoice_city: {
    title: Translate.string('Billing Address: City'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  // TEMA END: Added special field for Rechungsadresse/Invoice Recipient
  // TEMA: Added special field for Rechungsadresse/Invoice Recipient
  invoice_street: {
    title: Translate.string('Billing Address: Street'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  // TEMA END: Added special field for Rechungsadresse/Invoice Recipient
  // TEMA: Added special field for Rechungsadresse/Invoice Recipient
  invoice_zip: {
    title: Translate.string('Billing Address: Zip'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  // TEMA END: Added special field for Rechungsadresse/Invoice Recipient
  // TEMA: Added a special field to add a new email to send the invoice to
  invoice_email: {
    title: Translate.string('Additional Billing Email'),
    inputComponent: EmailInput,
    icon: 'mail',
  },
  // TEMA END: Added a special field to add a new email to send the invoice to
  // TEMA: Added special field for Member-IDs
  ext__member_id: {
    title: Translate.string('Member-ID'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  // TEMA END: Added special field for Member-IDs
  //  TEMA: Added special fields to handle a contact person for a registration
  contact_person_email: {
    title: Translate.string('Contact Person Email'),
    inputComponent: EmailInput,
    icon: 'mail',
  },
  contact_person_first_name: {
    title: Translate.string('Contact Person First Name'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  contact_person_last_name: {
    title: Translate.string('Contact Person Last Name'),
    inputComponent: TextInput,
    settingsComponent: TextSettings,
    settingsFormValidator: textSettingsFormValidator,
    icon: 'textfield',
  },
  contact_person_salutation: {
    title: Translate.string('Contact Person Salutation'),
    inputComponent: SingleChoiceInput,
    settingsComponent: SingleChoiceSettings,
    settingsModalSize: 'small',
    settingsFormDecorators: [choiceFieldsSettingsFormDecorator, singleChoiceSettingsFormDecorator],
    settingsFormInitialData: singleChoiceSettingsInitialData,
    icon: 'dropmenu',
  },
  //  TEMA END: Added special fields to handle a contact person for a registration
};

export function getFieldRegistry() {
  const pluginFields = Object.fromEntries(
    getPluginObjects('regformCustomFields').map(({name, ...rest}) => [name, rest])
  );
  if (Object.keys(pluginFields).some(x => !x.startsWith('ext__'))) {
    throw new Error('Field names from plugins must begin with `ext__`');
  }
  return {...fieldRegistry, ...pluginFields};
}
